class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    window.addEventListener('DOMContentLoaded', () => {
      this.setHeaderHeight();
      this.setAnnouncementBarVisibleHeight();
    })

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    };

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.searchModal = this.querySelector('details-modal.header__search details');
    this.megaMenus = this.querySelectorAll('mega-menu');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => this.preventReveal = true;

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', () => {
      this.onScrollHandler();
      this.setAnnouncementBarVisibleHeight();
    }, false);

    this.createObserver();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  setAnnouncementBarVisibleHeight() {
    const announcementBarClientRects = document.querySelector('.announcement-bar').getBoundingClientRect();
    document.documentElement.style.setProperty('--announcement-bar-visible-height', `${announcementBarClientRects.bottom > 0 ? announcementBarClientRects.bottom : 0 }px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.searchModal && this.searchModal.hasAttribute('open')) return;
    if (this.megaMenus.length && Array.from(this.megaMenus).some(menu => menu.isOpen())) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal.header__search');
    this.searchModal.close(false);
  }
}

customElements.define('sticky-header', StickyHeader);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.opener = this.querySelector('[data-menu-opener]');
    this.header = document.querySelector('.section-header');
    this.overlay = this.header.querySelector('.header-overlay');

    this.ariaControls = this.opener.getAttribute('aria-controls');
    this.megaMenu = document.querySelector(`#${this.ariaControls}`);

    this.megaMenuOpenBtn = this.querySelector('.mega-menu__open-btn');
    this.megaMenuCloseBtn =  this.megaMenu.querySelector('.mega-menu__close-btn');

    this.searchModal = document.querySelector('details-modal.header__search details');

    this.megaMenus = document.querySelectorAll('mega-menu');
  }

  connectedCallback() {
    this.eventListeners();
    this.setMegaMenuHeight();
  }

  eventListeners() {
    this.addEventListener('mouseover', this.onMouseOver.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));

    this.megaMenuOpenBtn.addEventListener('click', this.open.bind(this));
    this.megaMenuCloseBtn.addEventListener('click', this.close.bind(this));

    this.megaMenu.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.close();
    });
  }

  setMegaMenuHeight() {
    let megaMenuHeight = 0;
    this.megaMenus.forEach(item => {
      if (item.megaMenu && item.megaMenu?.offsetHeight > megaMenuHeight) megaMenuHeight = item.megaMenu?.offsetHeight
    })
    document.documentElement.style.setProperty('--mega-menu-height', `${megaMenuHeight}px`);
  }

  onMouseOver() {this.open()}
  onMouseLeave() {this.close()}

  isOpen() {
    return this.opener.getAttribute('aria-expanded') === 'true';
  }

  open() {
    if ((this.isOpen() || !this.megaMenu) || (this.searchModal && this.searchModal.hasAttribute('open'))) return;

    this.closeAll();
    this.megaMenu.inert = false;

    this.header?.classList.add('menu-opened');
    this.opener.setAttribute('aria-expanded', 'true');
    this.megaMenu.setAttribute('aria-hidden', 'false');
    this.overlay?.setAttribute('aria-hidden', 'false');

    this.megaMenu.addEventListener('transitionend', () => {
      trapFocus(this.megaMenu, this.megaMenuCloseBtn);
    }, {once: true})
  }

  close() {
    if (!this.isOpen() || !this.megaMenu) return

    removeTrapFocus(this.megaMenuOpenBtn);
    this.megaMenu.inert = true;

    this.header?.classList.remove('menu-opened');
    this.megaMenu.setAttribute('aria-hidden', 'true');
    this.opener.setAttribute('aria-expanded', 'false');
    this.overlay?.setAttribute('aria-hidden', 'true');
  }

  closeAll() {
    this.megaMenus.forEach(item => {
      if (item.ariaControls === this.ariaControls) return;

      item.megaMenu.inert = true;
      item.megaMenu.setAttribute('aria-hidden', 'true');
      item.opener.setAttribute('aria-expanded', 'false');
    })
  }
}

customElements.define('mega-menu', MegaMenu);

class MegaMenuContent extends MegaMenu {
  constructor() {
    super();
    this.overlay = null;
    this.header = null;

    // leave this variable as it is used for closeAll method (inherited from parent)
    this.megaMenus = this.closest('mega-menu').querySelectorAll('mega-menu-content');
  }

  onMouseLeave() {}
  setMegaMenuHeight() {}
}

customElements.define('mega-menu-content', MegaMenuContent);

class HeaderDrawer extends HTMLElement {
  constructor() {
    super();

    this.header = document.querySelector('.section-header');
    this.opener = this.querySelector('.header__icon--menu');
    this.drawer = this.querySelector('#menu-drawer');
    this.overlay = this.header.querySelector('.header-overlay');
    this.allowScrollElem = this.querySelector('[data-enable-scroll]') || this.drawer;

    this.opened = false;

    this.eventListeners();
  }

  eventListeners() {
    this.opener.addEventListener('click', this.toggle.bind(this));
    this.overlay.addEventListener('click', () => {
      this.toggle({close: true})
    });

    this.drawer.addEventListener('keyup', (e) => {
      if (e.code.toUpperCase() === 'ESCAPE') this.toggle({close: true});
    });
  }

  toggle({close = false} = {}) {
    if (close) this.opened = true;
    this.opened = !this.opened;

    this.header.classList.toggle('header-drawer-opened', this.opened);
    this.opener.setAttribute('aria-expanded', this.opened);
    this.drawer.setAttribute('aria-hidden', !this.opened);
    this.overlay.setAttribute('aria-hidden', !this.opened);

    if (this.opened) {
      document.body.classList.add('overflow-hidden-tablet');
      this.drawer.addEventListener('transitionend', () => {
        trapFocus(this.drawer, this.getFirstFocusableElement());
      }, {once: true})
    } else {
      removeTrapFocus(this.opener);
      document.body.classList.remove('overflow-hidden-tablet');
    }
  }

  getFirstFocusableElement() {
    return getFocusableElements(this.drawer)[0];
  }
}

customElements.define('header-drawer', HeaderDrawer);
